import type { Style } from "@tento-ui/component-styles";
import type { ChatBlockPlugin } from "./chat-block-plugin";
import { ChatBubble } from "./chat-bubble";
import { ChatDebugTurn } from "./chat-debug-turn";
import { ChatPlainTurn } from "./chat-plain-turn";
import { ChatTermTurn } from "./chat-term-turn";
import type { ChatTurn } from "./chat-turn";

// A theme is WHAT A TURN RENDERS AS plus WHAT THE THREAD LOOKS LIKE, named.
//
// The thread never cared which element draws a turn — it scrolls, recycles,
// and streams against the `ChatTurn` contract — so the whole difference
// between a bubbled messenger, a bare document, and a terminal is one factory
// plus one stylesheet. Naming the pair is what makes the choice a THEME
// rather than a wiring detail: a host picks a register in one line, and
// every register is proven against the same thread, the same plugins, the
// same stream.
//
//   bubbles   the messenger register: role-aligned chrome around each turn —
//             the user's turns on a tinted ground, the assistant's bare.
//             Every property of the user chrome is a --chat-bubble-* hook,
//             so this one register carries the product chat, a chatgpt.com
//             replica, and a claude.ai replica — sheets, not subclasses.
//   plain     the document register: the same content full-width with no
//             chrome at all, prose spacing between turns. Who is speaking is
//             carried by the content, not by a box.
//   tui       the terminal register: a turn is a collection of equal-height
//             lines and nothing else. The pane around the thread owns the
//             ground and the gutter, exactly as a terminal window does.
//   debug     the developer register: a turn is its own ChatMessage,
//             stringified — the wire form of the conversation, live.
//
// The registers ship style-NEUTRAL (`$style: null`): the library owns the
// shapes, the app owns the looks. A branded look — "Claude CLI", "ChatGPT" —
// is a register spread under a name and a sheet:
//
//   const phosphor: ChatTheme = {
//     ...ChatThemes.tui(),
//     name: "phosphor",
//     $style: css([`:host { background: #04140a; color: #49e078; }`]),
//   };
//
// That is the whole cost of a look that only RE-SKINS a register. A look that
// speaks its own grammar — codex, claude-cli — pays for two more members it
// declares itself: a `makeTurn` naming its own turn element, and a `plugins()`
// carrying the blocks that element routes onto. Both live under `looks/`;
// nothing in this directory learns their names. Swapping at runtime goes
// through `ChatThread.setTheme`, which drains the register-shaped cell pool
// and re-renders — see the decision note there.
export interface ChatTheme {
  readonly name: string;
  /**
   * A stylesheet the thread adopts into its own shadow root while the theme
   * is active (LonaWebComponent.addStyle), removed on swap. It is real CSS
   * with the thread as `:host`, and because turn elements live inside the
   * thread's shadow tree it selects them directly too
   * (`std-chat-bubble[user] { … }`). What it cannot pierce — the turns' and
   * blocks' own shadow internals — is reached the way shadow DOM is always
   * reached: the custom-property hooks each component documents
   * (`--chat-terminal-*` on the terminal block, `--session-replay-ansi-*`
   * for the SGR palette, `--chat-plain-turn-gap` for document spacing),
   * set from this sheet and inherited down.
   *
   * A sheet MUST NOT pad the thread's `:host`. The thread is chromeless by
   * contract: each ROW insets itself horizontally through
   * `--chat-turn-inset` (every register consumes it; the bubble defaults it
   * to the product's 16px), and the vertical breathing room is the scroll
   * infrastructure's — `--thread-top-inset` / `--thread-bottom-inset` map
   * onto the scroll viewport's padding, so rows scroll THROUGH that zone
   * as a buffer instead of being clipped at a static frame. The host adds
   * chrome overlaid on the thread (a floating composer) through the
   * separate `--thread-top/bottom-occlusion` vars — summed with the
   * look's insets, never fighting them.
   */
  readonly $style: Option<Style.Compat>;
  makeTurn(): ChatTurn;
  /**
   * The block renderers this theme's grammar needs. {@link ChatThread.setTheme}
   * hands them to the plugin manager as one total call, so they are live for
   * exactly as long as the theme is bound and gone the instant it is not.
   *
   * This is what keeps a dialect OUT of the thread. A theme whose turn
   * element routes blocks onto types of its own must supply the plugins that
   * answer them — rather than the thread registering every dialect it has
   * ever heard of on every instance. The four registers below return nothing,
   * because a register is a mechanism and a grammar is a look's to speak.
   */
  plugins(): readonly ChatBlockPlugin[];
}

export namespace ChatThemes {
  /** The messenger register. `bubbles()` is today's product chat, unchanged. */
  export function bubbles(): ChatTheme {
    return {
      name: "bubbles",
      $style: null,
      plugins: () => [],
      makeTurn: () => ChatBubble.make() as ChatBubble,
    };
  }

  /** The document register: full-width, chromeless, prose-spaced. */
  export function plain(): ChatTheme {
    return {
      name: "plain",
      $style: null,
      plugins: () => [],
      makeTurn: () => ChatPlainTurn.make() as ChatPlainTurn,
    };
  }

  /** The terminal register: a turn is rows, the pane owns everything else. */
  export function tui(): ChatTheme {
    return {
      name: "tui",
      $style: null,
      // None. `ChatTermTurn` routes every block at its word, so a bare
      // terminal register renders text as text and a tool call as the shared
      // card. The bullet-and-connector grammar this used to carry was the
      // CODEX CLI's, and it now ships with the codex look that speaks it.
      plugins: () => [],
      makeTurn: () => ChatTermTurn.make() as ChatTermTurn,
    };
  }

  /** The developer register: every turn printed as the object it is. */
  export function debug(): ChatTheme {
    return {
      name: "debug",
      $style: null,
      plugins: () => [],
      makeTurn: () => ChatDebugTurn.make() as ChatDebugTurn,
    };
  }
}
